import { evaluateTable, parseNumber } from './table';
/** In the order the chart picker offers them: the common ones first. */
export const CHART_KINDS = [
    'column',
    'bar',
    'line',
    'area',
    'pie',
    'donut',
    'stacked',
    'compare',
    'kpi',
    'gauge',
    'progress',
    'funnel',
    'gantt',
    'radar',
];
export const CHART_LIMITS = {
    points: 24,
    series: 6,
    label: 40,
    seriesName: 30,
    unit: 8,
    /** A pie shows this many slices; the rest are folded into one "other". */
    slices: 6,
};
/** How many series each kind can draw. More are kept in the document, drawn by nobody. */
export const CHART_SERIES = {
    column: 1,
    bar: 1,
    line: 4,
    area: 1,
    pie: 1,
    donut: 1,
    stacked: 6,
    compare: 2,
    radar: 3,
    gauge: 1,
    progress: 1,
    funnel: 1,
    kpi: 1,
    gantt: 2,
};
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function finite(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}
function text(value, limit) {
    return typeof value === 'string' ? value.slice(0, limit) : '';
}
const SIZES = ['small', 'medium', 'large'];
/** The chart a metadata value describes, clamped — or nothing, if it is not one. */
export function parseChart(value) {
    const chart = CHART_KINDS.includes(value.chart)
        ? value.chart
        : 'column';
    if (!Array.isArray(value.points))
        return undefined;
    const points = [];
    for (const point of value.points.slice(0, CHART_LIMITS.points)) {
        if (!isRecord(point))
            continue;
        const number = finite(point.value);
        if (number === undefined)
            continue;
        points.push({ label: text(point.label, CHART_LIMITS.label), value: number });
    }
    if (points.length === 0)
        return undefined;
    let series;
    if (Array.isArray(value.series)) {
        series = value.series
            .slice(0, CHART_LIMITS.series)
            .filter(isRecord)
            .map((entry) => ({
            name: text(entry.name, CHART_LIMITS.seriesName),
            values: points.map((_, index) => Array.isArray(entry.values) ? (finite(entry.values[index]) ?? 0) : 0),
        }));
        // The first series is the points, whatever the stored copy says: two sources of one number
        // disagree the first time one of them is edited by an older version.
        const first = series[0];
        if (first !== undefined)
            series[0] = { ...first, values: points.map((point) => point.value) };
        if (series.length < 2)
            series = undefined;
    }
    const unit = text(value.unit, CHART_LIMITS.unit).trim();
    const max = finite(value.max);
    const source = parseSource(value.source);
    return {
        kind: 'chart',
        chart,
        points,
        size: SIZES.includes(value.size) ? value.size : 'medium',
        ...(series === undefined ? {} : { series }),
        ...(unit === '' ? {} : { unit }),
        ...(max === undefined || max <= 0 ? {} : { max }),
        ...(source === undefined ? {} : { source }),
    };
}
function parseSource(value) {
    if (!isRecord(value) || typeof value.nodeId !== 'string' || value.nodeId === '')
        return undefined;
    const labelColumn = finite(value.labelColumn);
    if (labelColumn === undefined || !Array.isArray(value.valueColumns))
        return undefined;
    const valueColumns = value.valueColumns
        .map((column) => finite(column))
        .filter((column) => column !== undefined && column >= 0)
        .map(Math.trunc)
        .slice(0, CHART_LIMITS.series);
    if (valueColumns.length === 0)
        return undefined;
    return {
        nodeId: value.nodeId.slice(0, 128),
        labelColumn: Math.max(0, Math.trunc(labelColumn)),
        valueColumns,
    };
}
/** Every series of a chart, one per value column, the first always present. */
export function chartSeries(content) {
    if (content.series !== undefined && content.series.length > 1)
        return content.series;
    return [{ name: '', values: content.points.map((point) => point.value) }];
}
/**
 * The chart with its numbers read from its table, when it has one and the table is there.
 *
 * Rows with no label and no number are skipped, so a table with empty rows at the bottom draws
 * the rows that have something in them. Formulas are shown as their results.
 */
export function resolveChart(content, tableOf, format) {
    const source = content.source;
    if (source === undefined)
        return content;
    const table = tableOf(source.nodeId);
    if (table === undefined)
        return content;
    const derived = chartFromTable(table, source, format);
    if (derived === undefined)
        return content;
    return {
        ...content,
        points: derived.points,
        ...(derived.series === undefined ? {} : { series: derived.series }),
    };
}
/** Points and series from a table's columns. `undefined` when no row has a number. */
export function chartFromTable(table, source, format) {
    const shown = evaluateTable(table.rows, format);
    const header = table.header ? shown[0] : undefined;
    const body = table.header ? shown.slice(1) : shown;
    const points = [];
    const values = source.valueColumns.map(() => []);
    for (const row of body) {
        if (points.length >= CHART_LIMITS.points)
            break;
        const label = (row[source.labelColumn] ?? '').trim().slice(0, CHART_LIMITS.label);
        const numbers = source.valueColumns.map((column) => parseNumber(row[column] ?? '', format));
        if (label === '' && numbers.every((number) => number === null))
            continue;
        if (numbers.every((number) => number === null))
            continue;
        points.push({ label, value: numbers[0] ?? 0 });
        numbers.forEach((number, index) => values[index]?.push(number ?? 0));
    }
    if (points.length === 0)
        return undefined;
    if (source.valueColumns.length < 2)
        return { points };
    return {
        points,
        series: source.valueColumns.map((column, index) => ({
            name: (header?.[column] ?? '').trim().slice(0, CHART_LIMITS.seriesName),
            values: values[index] ?? [],
        })),
    };
}
/** The top of a gauge or a ring: the stored one, 100 for a percentage, else the largest value. */
export function chartMax(content) {
    if (content.max !== undefined)
        return content.max;
    if (content.unit === '%')
        return 100;
    const largest = Math.max(...content.points.map((point) => point.value), 0);
    return largest > 0 ? largest : 1;
}
const PREFIX_UNITS = new Set(['R$', 'US$', '$', '€', '£']);
/**
 * A number as a person reads it on a chart: short, in their language, with its unit.
 *
 * `1234` is "1,2 mil" in Portuguese and "1.2K" in English; `3_500_000` is "3,5 mi" or "3.5M".
 * Currency units go in front, a percent sign right after, anything else after a space.
 */
export function formatChartValue(value, options = {}) {
    const portuguese = options.format?.decimal === ',';
    const absolute = Math.abs(value);
    let body;
    if (options.compact !== false && absolute >= 1_000) {
        const scales = [
            [1e9, ' bi', 'B'],
            [1e6, ' mi', 'M'],
            [1e3, ' mil', 'K'],
        ];
        const [scale, pt, en] = scales.find(([limit]) => absolute >= limit) ?? [1, '', ''];
        const scaled = Math.round((value / scale) * 10) / 10;
        body = `${decimal(scaled, portuguese)}${portuguese ? pt : en}`;
    }
    else {
        body = decimal(Math.round(value * 100) / 100, portuguese);
    }
    const unit = options.unit?.trim() ?? '';
    if (unit === '')
        return body;
    if (PREFIX_UNITS.has(unit))
        return value < 0 ? `-${unit} ${body.slice(1)}` : `${unit} ${body}`;
    if (unit === '%')
        return `${body}%`;
    return `${body} ${unit}`;
}
function decimal(value, portuguese) {
    const textual = String(value);
    return portuguese ? textual.replace('.', ',') : textual;
}
/** A chart that shows the kind at its best, so the picker's preview is never an empty grid. */
export function sampleChart(chart, portuguese = true) {
    const t = (pt, en) => (portuguese ? pt : en);
    const months = portuguese
        ? ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun']
        : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const base = { kind: 'chart', chart, size: 'medium' };
    const points = (labels, values) => labels.map((label, index) => ({ label, value: values[index] ?? 0 }));
    switch (chart) {
        case 'column':
        case 'line':
            return { ...base, points: points(months, [42, 55, 48, 70, 66, 84]) };
        case 'area':
            return { ...base, points: points(months, [12, 18, 15, 26, 31, 38]) };
        case 'bar':
            return {
                ...base,
                points: points([
                    t('Cozinha', 'Kitchen'),
                    t('Banheiro', 'Bathroom'),
                    t('Quarto', 'Bedroom'),
                    t('Sala', 'Living room'),
                ], [9, 6, 4, 3]),
            };
        case 'pie':
        case 'donut':
            return {
                ...base,
                points: points([
                    t('Moradia', 'Housing'),
                    t('Mercado', 'Groceries'),
                    t('Transporte', 'Transport'),
                    t('Lazer', 'Leisure'),
                ], [45, 25, 18, 12]),
                unit: '%',
            };
        case 'stacked': {
            const quarters = ['T1', 'T2', 'T3', 'T4'];
            const online = [30, 38, 45, 52];
            return {
                ...base,
                points: points(quarters, online),
                series: [
                    { name: 'Online', values: online },
                    { name: t('Loja', 'Store'), values: [40, 36, 35, 33] },
                ],
            };
        }
        case 'compare': {
            const labels = [t('Sono', 'Sleep'), t('Leitura', 'Reading'), t('Treino', 'Workout')];
            const before = [5, 1, 2];
            return {
                ...base,
                points: points(labels, before),
                series: [
                    { name: t('Antes', 'Before'), values: before },
                    { name: t('Depois', 'After'), values: [7.5, 4, 5] },
                ],
                unit: 'h',
            };
        }
        case 'radar': {
            const skills = [
                t('Lógica', 'Logic'),
                t('Design', 'Design'),
                t('Escrita', 'Writing'),
                t('Dados', 'Data'),
                t('Fala', 'Speaking'),
            ];
            const now = [7, 4, 6, 8, 5];
            return {
                ...base,
                points: points(skills, now),
                series: [
                    { name: t('Hoje', 'Today'), values: now },
                    { name: t('Meta', 'Goal'), values: [8, 7, 8, 9, 8] },
                ],
                max: 10,
            };
        }
        case 'gauge':
            return { ...base, points: points([t('Meta do mês', 'Monthly goal')], [72]), unit: '%' };
        case 'progress':
            return {
                ...base,
                points: points([t('Leitura', 'Reading'), t('Treinos', 'Workouts'), t('Economia', 'Savings')], [80, 55, 35]),
                unit: '%',
            };
        case 'funnel':
            return {
                ...base,
                points: points([
                    t('Visitas', 'Visits'),
                    t('Cadastros', 'Sign-ups'),
                    t('Testes', 'Trials'),
                    t('Clientes', 'Customers'),
                ], [1200, 480, 190, 75]),
            };
        case 'kpi':
            return {
                ...base,
                points: points(months, [3100, 3350, 2980, 3620, 3900, 4280]),
                unit: 'R$',
            };
        case 'gantt': {
            const tasks = [
                t('Pesquisa', 'Research'),
                t('Protótipo', 'Prototype'),
                t('Testes', 'Testing'),
                t('Lançamento', 'Launch'),
            ];
            const start = [0, 2, 5, 7];
            return {
                ...base,
                points: points(tasks, start),
                series: [
                    { name: t('Início', 'Start'), values: start },
                    { name: t('Fim', 'End'), values: [3, 6, 8, 8.5] },
                ],
                unit: t('sem', 'wk'),
            };
        }
    }
}
//# sourceMappingURL=chart.js.map